import React, { useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import { MediaPlayer, MediaProvider, Poster, Track } from '@vidstack/react';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import { storageHelper } from '../../Helpers/storageHelper';
import './VideoViewerV2.css';

const VideoViewerV2 = ({
    url,
    src,
    poster,
    title = "Video Player",
    tracks = [],
    thumbnails,
    autoPlay = false,
    loop = false,
    muted = false,
    preload = "metadata",
    crossOrigin,
    storage = "emthany-player-storage",
    colorScheme = "dark",
    className = "",
    style = {},
    videoId,
    isWatched = false,
    onPlay,
    onPause,
    onEnded,
    onTimeUpdate,
    onProgress,
    onError,
    onVolumeChange,
    onFullscreenChange,
    onView,
    onFinish,
    onSeek,
    onQualityChange,
    onSpeedChange,
    onClose,
    ...props
}) => {
    const playerRef = useRef(null);
    const hasTriggeredView = useRef(false);
    const hasTriggeredFinish = useRef(false);

    const videoSrc = url || src;
    const userId = storageHelper.getItem("user_id") || "guest";
    const storageKey = `${storage}-${userId}`;

    // Handle play event
    const handlePlay = useCallback((nativeEvent) => {
        if (!hasTriggeredView.current && onView) {
            hasTriggeredView.current = true;
            onView(videoId);
        }
        if (onPlay) onPlay(nativeEvent);
    }, [onPlay, onView, videoId]);

    // Handle pause event
    const handlePause = useCallback((nativeEvent) => {
        if (onPause) onPause(nativeEvent);
    }, [onPause]);

    // Handle ended event
    const handleEnded = useCallback((nativeEvent) => {
        if (!hasTriggeredFinish.current) {
            hasTriggeredFinish.current = true;
            if (onFinish) onFinish(videoId);
        }
        if (onEnded) onEnded(nativeEvent);
    }, [onEnded, onFinish, videoId]);

    // Handle time update event
    const handleTimeUpdate = useCallback((detail, nativeEvent) => {
        const currentTime = detail.currentTime || 0;
        const duration = detail.duration || 0;

        if (onTimeUpdate) onTimeUpdate(currentTime);

        if (duration > 0) {
            const progressRatio = currentTime / duration;
            if (onProgress) onProgress(progressRatio);

            // Check if 90% watched
            if (progressRatio >= 0.9 && !isWatched && !hasTriggeredFinish.current) {
                hasTriggeredFinish.current = true;
                if (onFinish) onFinish(videoId);
            }
        }
    }, [onTimeUpdate, onProgress, isWatched, onFinish, videoId]);

    // Handle volume change
    const handleVolumeChange = useCallback((detail) => {
        if (onVolumeChange) onVolumeChange(detail.volume, detail.muted);
    }, [onVolumeChange]);

    // Handle fullscreen change
    const handleFullscreenChange = useCallback((isFullscreen) => {
        if (onFullscreenChange) onFullscreenChange(isFullscreen);
    }, [onFullscreenChange]);

    // Handle error
    const handleError = useCallback((detail) => {
        if (onError) onError(detail);
    }, [onError]);

    // Handle rate change
    const handleRateChange = useCallback((detail) => {
        if (onSpeedChange) onSpeedChange(detail);
    }, [onSpeedChange]);

    // Handle seeked
    const handleSeeked = useCallback((detail) => {
        if (onSeek) onSeek(detail);
    }, [onSeek]);

    // Reset flags when videoSrc changes
    useEffect(() => {
        hasTriggeredView.current = false;
        hasTriggeredFinish.current = false;
    }, [videoSrc]);

    if (!videoSrc) {
        return (
            <div className="video-viewer-v2-error">
                <p>No video source provided.</p>
            </div>
        );
    }

    return (
        <div className={`video-viewer-v2-wrapper ${className}`} style={style}>
            {onClose && (
                <button
                    type="button"
                    className="video-viewer-v2-close-btn"
                    onClick={onClose}
                    title="إغلاق"
                    aria-label="إغلاق"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            )}
            <MediaPlayer
                ref={playerRef}
                title={title}
                src={{ src: videoSrc, type: 'video/mp4' }}
                poster={poster}
                autoplay={autoPlay}
                loop={loop}
                muted={muted}
                preload={preload}
                playsInline
                {...(crossOrigin ? { crossOrigin } : {})}
                storage={storageKey}
                keyTarget="document"
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onTimeUpdate={handleTimeUpdate}
                onVolumeChange={handleVolumeChange}
                onFullscreenChange={handleFullscreenChange}
                onError={handleError}
                onRateChange={handleRateChange}
                onSeeked={handleSeeked}
                className="video-viewer-v2-player"
                {...props}
            >
                <MediaProvider>
                    {poster && (
                        <Poster
                            className="vds-poster"
                            src={poster}
                            alt={title}
                        />
                    )}
                    {tracks && tracks.map((track, idx) => (
                        <Track
                            key={track.src || idx}
                            src={track.src}
                            kind={track.kind || 'subtitles'}
                            label={track.label}
                            lang={track.lang}
                            default={track.default}
                        />
                    ))}
                </MediaProvider>

                <DefaultVideoLayout
                    icons={defaultLayoutIcons}
                    thumbnails={thumbnails}
                    colorScheme={colorScheme}
                />
            </MediaPlayer>
        </div>
    );
};

export default VideoViewerV2;
